import { getErrMsg } from '../../lib/errorMsg';
import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Plus, X, Lock, History, Pencil } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { CostRevisionForm, CostRevisionHistory } from '../../components/CostRevision';
import { EMPTY_PART } from './helpers';
import { PartImagesPanel } from './PartImagesPanel';

const fmtRs = (n) => n != null ? `₹${Number(n).toFixed(2)}` : '—';
const fmtRs4 = (n) => n != null ? `₹${Number(n).toFixed(4)}` : '—';

/**
 * Seed the Overhead (₹) field. If the part has no overhead set yet but its components
 * don't add up to the PO price, pre-fill Overhead with that leftover so the costing
 * opens reconciled. Parts that already have an overhead value are left untouched.
 */
function seedOverhead(part) {
  const existing = parseFloat(part.overhead_fixed) || 0;
  if (existing > 0) return part.overhead_fixed;
  const gw = parseFloat(part.gross_weight) || 0;
  const nw = parseFloat(part.net_weight) || 0;
  const rm = parseFloat(part.rm_price_per_kg) || 0;
  const sc = parseFloat(part.scrap_price_per_kg) || 0;
  const rec = (part.scrap_recovery_pct != null ? Number(part.scrap_recovery_pct) : 100) / 100;
  const netRmc = gw * rm - Math.max(gw - nw, 0) * sc * rec;
  const proc = (part.processes || []).reduce((s, p) => s + (parseFloat(p?.cost) || 0), 0);
  const plating = parseFloat(part.plating_cost) || 0;
  const bop = (part.bop_items || []).reduce((s, b) => s + (parseFloat(b?.cost) || 0), 0);
  const life = parseInt(part.tooling_life) || 10000;
  const toolPerPc = life > 0 ? (parseFloat(part.tooling_cost) || 0) / life : 0;
  const selling = parseFloat(part.selling_price) || 0;
  const leftover = selling - netRmc - proc - plating - bop - toolPerPc;
  return leftover > 0.01 ? Math.round(leftover * 10000) / 10000 : (part.overhead_fixed ?? '');
}

export function PartModal({ part, materials, customers = [], onClose, onSaved, onGoToRMRevision }) {
  const [form, setForm] = useState(part ? {
    ...part,
    processes: part.processes?.length ? part.processes : [{ name: '', cost: '' }],
    plating_cost: part.plating_cost || '',
    bop_items: part.bop_items?.length ? part.bop_items : [{ name: '', cost: '' }],
    // Auto-allocate any current unallocated balance into Overhead so the sheet opens reconciled.
    overhead_fixed: seedOverhead(part),
  } : EMPTY_PART);
  const [saving, setSaving] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const isEdit = !!part?.part_id;
  const { user } = useAuth();

  // Revision history (Process Route + Plating). Session-queued records are sent on save and
  // merged with the part's stored history for the viewer.
  const [processRevisions, setProcessRevisions] = useState([]);
  const [platingRevisions, setPlatingRevisions] = useState([]);
  const [overheadRevisions, setOverheadRevisions] = useState([]);
  const [reviseTarget, setReviseTarget] = useState(null); // {kind:'process'|'plating'|'overhead', idx?, name?, prev}
  const [historyType, setHistoryType] = useState(null);   // 'process' | 'plating' | 'overhead'
  const origProcessNames = new Set((part?.processes || []).map(p => (p.name || '').trim().toLowerCase()));

  const applyRevision = (rec) => {
    if (reviseTarget?.kind === 'process') {
      setProcess(reviseTarget.idx, 'cost', rec.revised_cost);
      setProcessRevisions(prev => [...prev, rec]);
    } else if (reviseTarget?.kind === 'plating') {
      setField('plating_cost', rec.revised_cost);
      setPlatingRevisions(prev => [...prev, { ...rec, process_name: undefined }]);
    } else if (reviseTarget?.kind === 'overhead') {
      setField('overhead_fixed', rec.revised_cost);
      setOverheadRevisions(prev => [...prev, { ...rec, process_name: undefined }]);
    }
    setReviseTarget(null);
  };

  const categories = [...new Set(materials.filter(m => m.material_type !== 'scrap').map(m => m.category))];
  const grades = [...new Set(materials.filter(m => m.category === form.material_category && m.material_type !== 'scrap').map(m => m.grade))];

  const setField = (k, v) => setForm(f => {
    const updated = { ...f, [k]: v };
    if (k === 'net_weight' || k === 'gross_weight') {
      const net = parseFloat(k === 'net_weight' ? v : f.net_weight) || 0;
      const gross = parseFloat(k === 'gross_weight' ? v : f.gross_weight) || 0;
      if (gross > 0 && net >= 0 && net <= gross) {
        updated.scrap_percentage = parseFloat(((gross - net) / gross * 100).toFixed(2));
      }
    }
    return updated;
  });

  // Live costing — recalculated on every form change
  const lc = (() => {
    const grossWt = parseFloat(form.gross_weight) || 0;
    const netWt = parseFloat(form.net_weight) || 0;
    const rmPrice = parseFloat(form.rm_price_per_kg) || 0;
    const scrapPrice = parseFloat(form.scrap_price_per_kg) || 0;
    const approved = parseFloat(form.approved_costing_price) || 0;
    const toolingCost = parseFloat(form.tooling_cost) || 0;
    const toolingLife = parseInt(form.tooling_life) || 10000;
    const processCost = form.processes.filter(p => p.name).reduce((s, p) => s + (parseFloat(p.cost) || 0), 0);
    const plating = parseFloat(form.plating_cost) || 0;
    const bopTotal = (form.bop_items || []).filter(b => b.name).reduce((s, b) => s + (parseFloat(b.cost) || 0), 0);
    const overhead = parseFloat(form.overhead_fixed) || 0;

    const grossRmc = grossWt * rmPrice;
    const scrapWt = Math.max(grossWt - netWt, 0);
    // Customer-agreed scrap recovery % (defaults to 100% when unset).
    const recPct = (form.scrap_recovery_pct != null && form.scrap_recovery_pct !== '') ? parseFloat(form.scrap_recovery_pct) : 100;
    const recovery = (isNaN(recPct) ? 100 : recPct) / 100;
    const scrapRecovery = scrapWt * scrapPrice * recovery;
    const netRmc = grossRmc - scrapRecovery;
    const toolPerPc = toolingLife > 0 ? toolingCost / toolingLife : 0;
    // Selling price = the plain SUM of every cost component (incl. Overhead). In Add mode the user
    // types the PO price directly; in Edit mode it's derived so changing a component moves it.
    const liveCost = netRmc + processCost + plating + bopTotal + toolPerPc + overhead;
    // Selling price = the plain SUM of every cost component (incl. Overhead). In Add mode the user
    // types the PO price directly; in Edit mode it's derived so changing a component moves it.
    const selling = isEdit ? liveCost : (parseFloat(form.selling_price) || 0);
    // Reconciliation balance (unallocated): PO price minus every cost component, incl. Overhead. Target ₹0.
    const trueMargin = selling - netRmc - processCost - plating - bopTotal - toolPerPc - overhead;
    // Add-on = PO above the 4 direct costs; Margin = Add-on + Unallocated balance (owner definition).
    const addOn = selling - netRmc - processCost - plating - bopTotal;
    const marginValue = addOn + trueMargin;
    const marginPct = selling > 0 ? (marginValue / selling * 100) : null;

    return {
      grossWt, netWt, scrapWt, rmPrice, scrapPrice, recoveryPct: (isNaN(recPct) ? 100 : recPct),
      grossRmc, scrapRecovery, netRmc,
      processCost, plating, bopTotal, toolPerPc, overhead,
      selling, approved, trueMargin, addOn, marginValue, marginPct,
      hasData: grossWt > 0 && rmPrice > 0,
    };
  })();

  // Keep the read-only Selling Price field (and the saved value) in sync with the live pass-through.
  useEffect(() => {
    if (!isEdit) return;
    const rounded = Math.round(lc.selling * 10000) / 10000;
    setForm(f => (Math.abs((parseFloat(f.selling_price) || 0) - rounded) > 0.0001 ? { ...f, selling_price: rounded } : f));
  }, [lc.selling, isEdit]);

  // One-click: absorb the current unallocated balance into Overhead so it reconciles to ₹0.
  const allocateToOverhead = () => {
    const newOverhead = Math.round(((parseFloat(form.overhead_fixed) || 0) + lc.trueMargin) * 10000) / 10000;
    setField('overhead_fixed', newOverhead);
  };

  const setProcess = (i, k, v) => setForm(f => ({
    ...f, processes: f.processes.map((p, idx) => idx === i ? { ...p, [k]: v } : p)
  }));
  const addProcess = () => setForm(f => ({ ...f, processes: [...f.processes, { name: '', cost: '' }] }));
  const removeProcess = (i) => setForm(f => ({ ...f, processes: f.processes.filter((_, idx) => idx !== i) }));

  const setBopItem = (i, k, v) => setForm(f => ({
    ...f, bop_items: (f.bop_items || []).map((b, idx) => idx === i ? { ...b, [k]: v } : b)
  }));
  const addBopItem = () => setForm(f => ({ ...f, bop_items: [...(f.bop_items || []), { name: '', cost: '' }] }));
  const removeBopItem = (i) => setForm(f => ({ ...f, bop_items: (f.bop_items || []).filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.part_number || !form.part_name || !form.material_category) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        net_weight: Number(form.net_weight),
        gross_weight: Number(form.gross_weight),
        scrap_percentage: Number(form.scrap_percentage) || 0,
        scrap_recovery_pct: (form.scrap_recovery_pct === '' || form.scrap_recovery_pct == null) ? 100 : Number(form.scrap_recovery_pct),
        tooling_cost: Number(form.tooling_cost) || 0,
        tooling_life: Number(form.tooling_life) || 10000,
        overhead_percentage: Number(form.overhead_percentage) || 15,
        overhead_fixed: Number(form.overhead_fixed) || 0,
        selling_price: Number(form.selling_price) || 0,
        rm_price_per_kg: Number(form.rm_price_per_kg) || 0,
        scrap_price_per_kg: Number(form.scrap_price_per_kg) || 0,
        thickness_mm: form.thickness_mm !== '' ? Number(form.thickness_mm) : null,
        plating_cost: Number(form.plating_cost) || 0,
        processes: form.processes.filter(p => p.name).map(p => ({ name: p.name, cost: Number(p.cost) || 0 })),
        bop_items: (form.bop_items || []).filter(b => b.name).map(b => ({ name: b.name, cost: Number(b.cost) || 0 })),
        ...(processRevisions.length ? { process_revisions: processRevisions } : {}),
        ...(platingRevisions.length ? { plating_revisions: platingRevisions } : {}),
        ...(overheadRevisions.length ? { overhead_revisions: overheadRevisions } : {}),
      };
      if (isEdit) {
        await api.put(`/parts/${part.part_id}`, payload);
        toast.success('Part updated successfully');
      } else {
        const res = await api.post('/parts', payload);
        const newId = res?.data?.part_id;
        if (newId && pendingImages.length) {
          let ok = 0;
          for (const f of pendingImages) {
            try {
              const fd = new FormData();
              fd.append('file', f);
              await api.post(`/parts/${newId}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
              ok += 1;
            } catch (e) {
              toast.error(`${f.name}: ${getErrMsg(e)}`);
            }
          }
          toast.success(`Part created${ok ? ` · ${ok} image${ok > 1 ? 's' : ''} uploaded` : ''}`);
        } else {
          toast.success('Part created successfully');
        }
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-900">
            {isEdit ? `Edit Part — ${form.part_number}` : 'Add New FG Part'}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 mt-2">
          {[
            { label: 'Part Number *', key: 'part_number', type: 'text', placeholder: 'AM-BKT-001' },
            { label: 'Part Name *', key: 'part_name', type: 'text', placeholder: 'Mounting Bracket L-Type' },
          ].map(({ label, key, type, placeholder }) => (
            <div key={key} className={key === 'part_name' ? 'col-span-2 md:col-span-1' : ''}>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">{label}</label>
              <input type={type} value={form[key]} onChange={e => setField(key, e.target.value)} placeholder={placeholder}
                data-testid={`part-form-${key}`}
                className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" />
            </div>
          ))}

          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Customer Name *</label>
            <select value={form.customer_name} onChange={e => setField('customer_name', e.target.value)}
              data-testid="part-form-customer_name"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900">
              <option value="">Select Customer</option>
              {customers.map(c => <option key={c} value={c}>{c}</option>)}
              {form.customer_name && !customers.includes(form.customer_name) && (
                <option value={form.customer_name}>{form.customer_name}</option>
              )}
            </select>
          </div>

          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">
              Material Category * <Lock size={10} className="inline text-orange-400 ml-1" title="Protected from imports" />
            </label>
            <select value={form.material_category} onChange={e => setField('material_category', e.target.value)}
              data-testid="part-form-material-category"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900">
              <option value="">Select Category</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">
              Material Grade * <Lock size={10} className="inline text-orange-400 ml-1" title="Protected from imports" />
            </label>
            <select value={form.material_grade} onChange={e => setField('material_grade', e.target.value)}
              data-testid="part-form-material-grade"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900">
              <option value="">Select Grade</option>
              {grades.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">Thickness (mm)</label>
            <input type="number" step="0.1" min="0" value={form.thickness_mm} onChange={e => setField('thickness_mm', e.target.value)} placeholder="e.g. 0.6"
              data-testid="part-form-thickness-mm"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-900" />
            <p className="text-[10px] text-slate-400 mt-1">Used for thickness-specific market price lookup</p>
          </div>

          {[
            { label: 'Net Weight (kg) *', key: 'net_weight', placeholder: '0.450', locked: true },
            { label: 'Gross Weight (kg) *', key: 'gross_weight', placeholder: '0.520', locked: true },
            { label: 'Scrap %', key: 'scrap_percentage', placeholder: '13.5', hint: 'Auto-calculated from Gross − Net', readOnly: true },
            { label: 'Locked RM Price (₹/kg)', key: 'rm_price_per_kg', placeholder: 'From costing sheet', readOnly: isEdit },
            { label: 'Scrap Price (₹/kg)', key: 'scrap_price_per_kg', placeholder: 'Scrap price for this part', readOnly: isEdit },
            { label: 'Scrap Recovery %', key: 'scrap_recovery_pct', placeholder: '100', hint: 'Agreed scrap credit % for this part (e.g. 85). Applied to Net RMC.' },
            { label: 'Tooling Cost (₹)', key: 'tooling_cost', placeholder: '85000' },
            { label: 'Tooling Life (pcs)', key: 'tooling_life', placeholder: '50000' },
            { label: 'Selling Price (PO) (₹) *', key: 'selling_price', placeholder: '95.00', readOnly: isEdit,
              hint: isEdit ? 'Auto-derived — moves with the cost components below (pass-through)' : undefined },
          ].map(({ label, key, placeholder, hint, locked, readOnly }) => (
            <div key={key}>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1.5 block">
                {label}
                {locked && <Lock size={10} className="inline text-orange-400 ml-1" title="Protected from imports" />}
                {readOnly && <Lock size={10} className="inline text-amber-500 ml-1" title="Locked — cannot be edited here" />}
              </label>
              <input type="number" step="any" value={form[key]}
                onChange={readOnly ? undefined : (e => setField(key, e.target.value))}
                readOnly={readOnly} disabled={readOnly} placeholder={placeholder}
                data-testid={`part-form-${key}`}
                className={`w-full border rounded-sm h-9 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-900 ${
                  readOnly ? 'border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed' : 'border-slate-300'
                }`} />
              {hint && <p className="text-[10px] text-slate-400 mt-1">{hint}</p>}
            </div>
          ))}
        </div>

        {/* Locked-fields notice — RM/scrap change only via RM Revision (keeps history) */}
        {isEdit && (
          <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm p-2.5">
            <Lock size={13} className="mt-0.5 shrink-0 text-amber-500" />
            <p className="text-xs text-amber-800">
              <strong>Locked RM, Scrap and Selling price can’t be edited here.</strong> To change RM/scrap rates use{' '}
              {onGoToRMRevision ? (
                <button type="button" onClick={onGoToRMRevision}
                  className="font-bold underline hover:text-amber-900" data-testid="goto-rm-revision">
                  RM Revision
                </button>
              ) : <span className="font-bold">RM Revision</span>}
              {' '}so history is preserved. The selling price isn’t typed directly — it’s derived from the cost components below, so raising a component (e.g. plating) raises the selling price by the same amount.
            </p>
          </div>
        )}

        {/* Part Images & Drawings (gallery) */}
        {isEdit ? (
          <PartImagesPanel partId={part.part_id} />
        ) : (
          <PartImagesPanel partId={null} onPendingChange={setPendingImages} />
        )}

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-bold uppercase text-slate-500">
              Process Route <Lock size={10} className="inline text-orange-400 ml-1" title="Protected from imports" />
            </label>
            <div className="flex items-center gap-3">
              {isEdit && (
                <button onClick={() => setHistoryType('process')} type="button"
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  data-testid="process-history-btn">
                  <History size={12} /> Process Route History
                </button>
              )}
              <button onClick={addProcess} type="button"
                className="text-xs font-semibold text-orange-500 hover:text-orange-600 flex items-center gap-1"
                data-testid="add-process-btn">
                <Plus size={12} /> Add Process
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {form.processes.map((proc, i) => {
              // Existing processes on a saved part change cost only via the revision form.
              const isExisting = isEdit && origProcessNames.has((proc.name || '').trim().toLowerCase());
              return (
              <div key={i} className="flex gap-2">
                <input type="text" value={proc.name} onChange={e => setProcess(i, 'name', e.target.value)}
                  placeholder={`Process name (e.g. Blanking)`}
                  className="flex-1 border border-slate-300 rounded-sm h-8 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900" />
                {isExisting ? (
                  <>
                    <div className="w-24 border border-slate-200 bg-slate-100 rounded-sm h-8 px-2 text-sm font-mono flex items-center justify-end text-slate-600">{fmtRs(Number(proc.cost) || 0)}</div>
                    <button type="button" title="Revise cost"
                      onClick={() => setReviseTarget({ kind: 'process', idx: i, name: proc.name, prev: proc.cost })}
                      data-testid={`revise-process-${i}`}
                      className="text-slate-400 hover:text-slate-800 transition-colors"><Pencil size={14} /></button>
                  </>
                ) : (
                  <input type="number" step="any" value={proc.cost} onChange={e => setProcess(i, 'cost', e.target.value)}
                    placeholder="₹/pc"
                    className="w-24 border border-slate-300 rounded-sm h-8 px-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-slate-900" />
                )}
                {form.processes.length > 1 && (
                  <button onClick={() => removeProcess(i)} type="button" className="text-slate-400 hover:text-red-500 transition-colors">
                    <X size={14} />
                  </button>
                )}
              </div>
              );
            })}
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-bold uppercase text-slate-500">Plating Cost (₹/pc)</label>
            {isEdit && (
              <button onClick={() => setHistoryType('plating')} type="button"
                className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
                data-testid="plating-history-btn">
                <History size={12} /> Plating Cost Revision History
              </button>
            )}
          </div>
          {isEdit ? (
            <div className="flex gap-2">
              <div className="flex-1 border border-slate-200 bg-slate-100 rounded-sm h-9 px-3 text-sm font-mono flex items-center text-slate-600" data-testid="part-form-plating-cost">{fmtRs(Number(form.plating_cost) || 0)}</div>
              <button type="button" title="Revise plating cost"
                onClick={() => setReviseTarget({ kind: 'plating', prev: form.plating_cost })}
                data-testid="revise-plating"
                className="flex items-center gap-1 px-3 h-9 border border-slate-300 rounded-sm text-slate-700 text-xs font-semibold hover:bg-slate-50">
                <Pencil size={13} /> Revise
              </button>
            </div>
          ) : (
            <input type="number" step="any" value={form.plating_cost} onChange={e => setField('plating_cost', e.target.value)}
              placeholder="Outsourced plating cost per piece"
              data-testid="part-form-plating-cost"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-900" />
          )}
          <p className="text-[10px] text-slate-400 mt-1">Outsourced cost — part of the costing sum. Revise to log who/when/why.</p>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-bold uppercase text-slate-500">Overheads (₹/pc)</label>
            {isEdit && (
              <button onClick={() => setHistoryType('overhead')} type="button"
                className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
                data-testid="overhead-history-btn">
                <History size={12} /> Overheads Revision History
              </button>
            )}
          </div>
          {isEdit ? (
            <div className="flex gap-2">
              <div className="flex-1 border border-slate-200 bg-slate-100 rounded-sm h-9 px-3 text-sm font-mono flex items-center text-slate-600" data-testid="part-form-overhead">{fmtRs(Number(form.overhead_fixed) || 0)}</div>
              <button type="button" title="Revise overheads"
                onClick={() => setReviseTarget({ kind: 'overhead', prev: form.overhead_fixed })}
                data-testid="revise-overhead"
                className="flex items-center gap-1 px-3 h-9 border border-slate-300 rounded-sm text-slate-700 text-xs font-semibold hover:bg-slate-50">
                <Pencil size={13} /> Revise
              </button>
            </div>
          ) : (
            <input type="number" step="any" value={form.overhead_fixed ?? ''} onChange={e => setField('overhead_fixed', e.target.value)}
              placeholder="Overhead per piece"
              data-testid="part-form-overhead"
              className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-900" />
          )}
          <p className="text-[10px] text-slate-400 mt-1">Absorbs the leftover so the costing reconciles to the PO price — auto-filled from the unallocated balance. Revise to log who/when/why.</p>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <label className="text-xs font-bold uppercase text-slate-500">Bought Out Parts (BOP)</label>
              <p className="text-[10px] text-slate-400 mt-0.5">External components purchased and added to the part</p>
            </div>
            <button onClick={addBopItem} type="button"
              className="text-xs font-semibold text-orange-500 hover:text-orange-600 flex items-center gap-1"
              data-testid="add-bop-btn">
              <Plus size={12} /> Add BOP
            </button>
          </div>
          <div className="space-y-2">
            {(form.bop_items || []).map((bop, i) => (
              <div key={i} className="flex gap-2" data-testid={`bop-item-${i}`}>
                <input type="text" value={bop.name} onChange={e => setBopItem(i, 'name', e.target.value)}
                  placeholder={`BOP name (e.g. Spring, Clip)`}
                  className="flex-1 border border-slate-300 rounded-sm h-8 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900" />
                <input type="number" step="any" value={bop.cost} onChange={e => setBopItem(i, 'cost', e.target.value)}
                  placeholder="₹/pc"
                  className="w-24 border border-slate-300 rounded-sm h-8 px-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-slate-900" />
                {(form.bop_items || []).length > 1 && (
                  <button onClick={() => removeBopItem(i)} type="button" className="text-slate-400 hover:text-red-500 transition-colors">
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Live Costing Breakdown — matches CostBreakdownModal layout */}
        <div className="mt-5 border border-slate-200 rounded-sm overflow-hidden" data-testid="live-costing-breakdown">
          <div className="bg-slate-800 px-4 py-2">
            <p className="text-[10px] uppercase font-bold text-slate-300 tracking-wider">Live Costing Breakdown</p>
          </div>
          <div className="divide-y divide-slate-100">
            {/* Gross RMC */}
            <div className="flex items-center justify-between px-4 py-2.5">
              <div>
                <p className="text-sm font-semibold text-slate-700">Gross RMC</p>
                <p className="text-[10px] text-slate-400">{lc.grossWt.toFixed(4)} kg × ₹{lc.rmPrice}/kg</p>
              </div>
              <p className="font-mono text-sm font-bold text-slate-900" data-testid="live-gross-rmc">{fmtRs4(lc.grossRmc)}</p>
            </div>

            {/* Scrap Recovery */}
            <div className="flex items-center justify-between px-4 py-2.5">
              <div>
                <p className="text-sm font-semibold text-emerald-700">Less: Scrap Recovery{lc.recoveryPct < 100 ? ` (${lc.recoveryPct}%)` : ''}</p>
                <p className="text-[10px] text-slate-400">{lc.scrapWt.toFixed(4)} kg × ₹{lc.scrapPrice}/kg{lc.recoveryPct < 100 ? ` × ${lc.recoveryPct}%` : ''}</p>
              </div>
              <p className="font-mono text-sm font-bold text-emerald-600" data-testid="live-scrap-recovery">−{fmtRs4(lc.scrapRecovery)}</p>
            </div>

            {/* Net RMC */}
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50">
              <div>
                <p className="font-bold text-slate-900">Net RMC</p>
                <p className="text-[10px] text-slate-400">Gross RMC − Scrap Recovery</p>
              </div>
              <p className="font-mono font-black text-lg text-slate-900" data-testid="live-net-rmc">{fmtRs4(lc.netRmc)}</p>
            </div>

            {/* Process Cost */}
            {lc.processCost > 0 && (
              <div className="flex items-center justify-between px-4 py-2.5">
                <p className="text-sm font-semibold text-slate-700">Process Cost</p>
                <p className="font-mono text-sm font-bold text-slate-900" data-testid="live-process-cost">{fmtRs(lc.processCost)}</p>
              </div>
            )}

            {/* Plating */}
            {lc.plating > 0 && (
              <div className="flex items-center justify-between px-4 py-2.5">
                <p className="text-sm font-semibold text-purple-700">Plating Cost</p>
                <p className="font-mono text-sm font-bold text-purple-600">{fmtRs(lc.plating)}</p>
              </div>
            )}

            {/* BOP */}
            {lc.bopTotal > 0 && (
              <div className="flex items-center justify-between px-4 py-2.5">
                <p className="text-sm font-semibold text-indigo-700">BOP Cost</p>
                <p className="font-mono text-sm font-bold text-indigo-600">{fmtRs(lc.bopTotal)}</p>
              </div>
            )}

            {/* Overhead */}
            {lc.overhead > 0 && (
              <div className="flex items-center justify-between px-4 py-2.5">
                <p className="text-sm font-semibold text-slate-600">Overheads</p>
                <p className="font-mono text-sm font-bold text-slate-700" data-testid="live-overhead">{fmtRs(lc.overhead)}</p>
              </div>
            )}

            {/* Tooling/pc */}
            {lc.toolPerPc > 0.001 && (
              <div className="flex items-center justify-between px-4 py-2.5">
                <p className="text-sm text-slate-600">Tooling / Piece</p>
                <p className="font-mono text-sm font-semibold text-slate-600">{fmtRs4(lc.toolPerPc)}</p>
              </div>
            )}

            {/* Selling Price */}
            <div className="flex items-center justify-between px-4 py-2.5 border-t-2 border-slate-200">
              <p className="text-sm font-semibold text-slate-700">Selling Price</p>
              <p className="font-mono text-sm font-bold text-slate-900" data-testid="live-selling-price">{fmtRs(lc.selling)}</p>
            </div>

            {/* Reconciliation — Selling (PO) minus the summed cost components. Target: ₹0. */}
            {(() => {
              const gap = lc.trueMargin; // Selling − Net RMC − Process − Plating − BOP − Tooling/pc
              const reconciled = Math.abs(gap) < 0.01;
              const over = gap < -0.01; // costs exceed the PO price
              const rowBg = reconciled ? 'bg-emerald-50' : over ? 'bg-red-50' : 'bg-amber-50';
              const valCls = reconciled ? 'text-emerald-700' : over ? 'text-red-700' : 'text-amber-700';
              const title = reconciled ? 'Reconciled' : over ? 'Over PO price' : 'Unallocated balance';
              const note = reconciled
                ? 'Cost components add up exactly to the PO price ✓'
                : over
                  ? 'Components exceed the PO price — reduce a component'
                  : 'Not fully allocated — add it to a component (or Overheads) until it reaches ₹0';
              return (
                <div className={`flex items-center justify-between px-4 py-3 ${rowBg}`} data-testid="live-reconciliation">
                  <div>
                    <p className="font-bold text-slate-900">{title}</p>
                    <p className="text-[10px] text-slate-500">
                      Selling (PO) − Net RMC − Process − Plating − BOP − Overheads{lc.toolPerPc > 0.001 ? ' − Tooling/pc' : ''}. {note}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-mono font-black text-lg ${valCls}`}>{fmtRs(gap)}</p>
                    {reconciled
                      ? <span className="inline-block px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-100 text-emerald-700">Balanced</span>
                      : <div className="flex items-center gap-2 justify-end mt-0.5">
                          <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold ${over ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{over ? 'Over by' : 'To allocate'}: {fmtRs(Math.abs(gap))}</span>
                          {!over && (
                            <button type="button" onClick={allocateToOverhead} data-testid="allocate-overhead-btn"
                              className="px-2 py-0.5 rounded text-[11px] font-bold bg-slate-900 text-white hover:bg-slate-800">
                              → Overheads
                            </button>
                          )}
                        </div>}
                  </div>
                </div>
              );
            })()}

            {/* Margin = Add-on + Unallocated balance */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200" data-testid="live-margin">
              <div>
                <p className="font-bold text-slate-900">Margin</p>
                <p className="text-[10px] text-slate-500">Add-on ({fmtRs(lc.addOn)}) + Unallocated ({fmtRs(lc.trueMargin)})</p>
              </div>
              <div className="text-right">
                <p className={`font-mono font-black text-lg ${lc.marginValue >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmtRs(lc.marginValue)}</p>
                {lc.marginPct !== null && (
                  <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold ${
                    lc.marginPct >= 15 ? 'bg-emerald-100 text-emerald-700' :
                    lc.marginPct >= 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                  }`}>{lc.marginPct.toFixed(1)}%</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button onClick={onClose} className="flex-1 h-9 border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            data-testid="save-part-btn"
            className="flex-1 h-9 bg-slate-900 text-white text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-slate-800 transition-colors disabled:opacity-50">
            {saving ? 'Saving...' : (isEdit ? 'Update Part' : 'Create Part')}
          </button>
        </div>
      </DialogContent>

      {reviseTarget && (
        <CostRevisionForm
          title={reviseTarget.kind === 'plating' ? 'Revise Plating Cost'
            : reviseTarget.kind === 'overhead' ? 'Revise Overheads' : 'Revise Process Cost'}
          processName={reviseTarget.kind === 'process' ? reviseTarget.name : undefined}
          previousCost={reviseTarget.prev}
          defaultBy={user?.name || ''}
          onSubmit={applyRevision}
          onClose={() => setReviseTarget(null)} />
      )}
      {historyType === 'process' && (
        <CostRevisionHistory title="Process Route — Revision History" showName
          records={[...(part?.process_cost_history || []), ...processRevisions]}
          onClose={() => setHistoryType(null)} />
      )}
      {historyType === 'plating' && (
        <CostRevisionHistory title="Plating Cost — Revision History"
          records={[...(part?.plating_cost_history || []), ...platingRevisions]}
          onClose={() => setHistoryType(null)} />
      )}
      {historyType === 'overhead' && (
        <CostRevisionHistory title="Overheads — Revision History"
          records={[...(part?.overhead_cost_history || []), ...overheadRevisions]}
          onClose={() => setHistoryType(null)} />
      )}
    </Dialog>
  );
}
